import React, { useContext, useState, useEffect } from "react";
import "../../Tabels/Tabels.css";
import { Context } from "../../../../utils/context";
import Offcanvas from "react-bootstrap/Offcanvas";
import Card from "react-bootstrap/Card";
import { Row, Col, Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

import ModalSave from "../../../common/ModalSave";
import { CancelButton, SaveButton } from "../../../common/Button";
import { Select2Data } from "../../../../utils/common";

const PAYMENT_TYPES = [
  { value: "UPI", label: "UPI" },
  { value: "Bank", label: "Bank" },
  { value: "Crypto", label: "Crypto" },
];

// react-select border turns red when the field has a validation error
const selectStyles = (hasError) => ({
  control: (base) => ({
    ...base,
    borderColor: hasError ? "red" : base.borderColor,
  }),
});

const FieldError = ({ error }) =>
  error ? <span className="text-danger">{error.message}</span> : null;

// Used for both "Add Bank" (no id) and "Edit Bank" (id)
const AddOffCanvance = (props) => {
  const { postData, getData, IMG_URL } = useContext(Context);
  const id = props.id;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm();

  const [showModal, setShowModal] = useState({ code: 0, message: "" });
  const [iconPreview, setIconPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [states, setStates] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [cities, setCity] = useState([]);

  const paymentType = watch("payment_type_id")?.value;
  const selectedDistricts = watch("selected_districts") || [];

  // ---- master data -------------------------------------------------------
  const GetAllState = async () => {
    const response = await getData("/without-login/masters/all-states");
    if (response?.success) {
      setStates(await Select2Data(response?.data, "state_id"));
    }
  };

  const GetAllNetwork = async () => {
    const response = await getData("/without-login/masters/all-networks");
    if (response?.success) {
      setNetworks(await Select2Data(response?.data, "network_id"));
    }
  };

  const GetAllCity = async (stateIds) => {
    try {
      const response = await getData(
        `/without-login/masters/all-cities?state_id=${stateIds.join(",")}`,
      );
      setCity(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    GetAllState();
    GetAllNetwork();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- edit mode: load the existing record ------------------------------
  const GetEditData = async () => {
    if (!id) return;

    const response = await getData(`/masters/bank-detail/${id}`);
    const data = response?.data;
    if (!data) return;

    const stateIds = data.state_id?.map((item) => item.value) || [];
    reset({ ...data, state_id: stateIds });

    if (stateIds.length) {
      GetAllCity(stateIds);
    }
    if (data.icon) {
      setIconPreview(IMG_URL + data.icon);
    }
  };

  useEffect(() => {
    GetEditData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---- icon upload -------------------------------------------------------
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  // ---- district checkboxes ----------------------------------------------
  const handleSelectAll = (e) => {
    const allIds = cities.flatMap((city) => city.cities.map((c) => c.id));

    setValue(
      "selected_districts",
      e.target.checked
        ? [...new Set([...selectedDistricts, ...allIds])]
        : selectedDistricts.filter((districtId) => !allIds.includes(districtId)),
    );
  };

  const handleDistrictChange = (e, districtId) => {
    setValue(
      "selected_districts",
      e.target.checked
        ? [...new Set([...selectedDistricts, districtId])]
        : selectedDistricts.filter((d) => d !== districtId),
    );
  };

  // ---- submit ------------------------------------------------------------
  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      const handledKeys = [
        "icon",
        "selected_districts",
        "state_id",
        "payment_type_id",
        "network_id",
      ];

      for (const key in data) {
        if (
          !handledKeys.includes(key) &&
          data[key] !== undefined &&
          data[key] !== null
        ) {
          formData.append(key, data[key]);
        }
      }

      if (selectedFile) {
        formData.append("icon", selectedFile);
      }
      if (data?.selected_districts?.length) {
        formData.append(
          "selected_districts",
          JSON.stringify(data.selected_districts),
        );
      }
      if (data?.state_id?.length) {
        formData.append("state_id", JSON.stringify(data.state_id));
      }
      if (data?.payment_type_id) {
        formData.append("payment_type_id", data.payment_type_id.value);
      }
      if (data?.payment_type_id?.value === "Crypto" && data?.network_id) {
        formData.append("network_id", data.network_id.value);
      }

      const url = id ? `/masters/bank-detail/${id}` : `/masters/bank-detail`;
      const response = await postData(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowModal({ code: response.code, message: response.message });
      setTimeout(() => {
        setShowModal({ code: 0, message: "" });
        props.handleClose();
      }, 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // Text input bound to react-hook-form with a required rule
  const textField = (name, label, placeholder, type = "text") => (
    <Col md={6} lg={3}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        placeholder={placeholder}
        isInvalid={!!errors[name]}
        {...register(name, { required: `${label} is required` })}
      />
      <FieldError error={errors[name]} />
    </Col>
  );

  return (
    <>
      <Offcanvas
        show={props.show}
        style={{ width: "80%" }}
        placement="end"
        onHide={props.handleClose}
      >
        <Offcanvas.Header closeButton />
        <Offcanvas.Body>
          <Card className="shadow-lg p-3 mb-5 bg-white rounded">
            <Card.Body>
              <Card.Title>{id ? "Edit Bank" : "Add Bank"}</Card.Title>
              <hr />
              <Form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="AppSetupclass"
              >
                <Row className="g-3 mb-4">
                  {/* States */}
                  <Col lg={8}>
                    <Form.Label>States</Form.Label>
                    <Controller
                      name="state_id"
                      control={control}
                      rules={{ required: "Select States" }}
                      render={({ field }) => (
                        <Select
                          isMulti
                          options={states}
                          value={states.filter((option) =>
                            field.value?.includes(option.value),
                          )}
                          onChange={(selectedOptions) => {
                            const values = selectedOptions
                              ? selectedOptions.map((opt) => opt.value)
                              : [];
                            field.onChange(values);
                            GetAllCity(values);
                          }}
                          styles={selectStyles(!!errors.state_id)}
                        />
                      )}
                    />
                    <FieldError error={errors.state_id} />
                  </Col>

                  {/* Cities / districts of the selected states */}
                  {cities.length > 0 && (
                    <Col xs={12}>
                      <Form.Check
                        type="checkbox"
                        id="select-all-districts"
                        label="Select All"
                        onChange={handleSelectAll}
                      />
                      {cities.map((val) => (
                        <div key={val?.id ?? val?.state_name} className="mt-3">
                          <p className="state_text">{val?.state_name}</p>
                          <Row>
                            {val?.cities?.map((val1) => (
                              <Col lg={3} key={val1?.id}>
                                <Form.Check
                                  id={`district-${val?.id}-${val1?.id}`}
                                  type="checkbox"
                                  label={val1?.name}
                                  value={val1?.id}
                                  checked={selectedDistricts.includes(val1.id)}
                                  onChange={(e) =>
                                    handleDistrictChange(e, val1.id)
                                  }
                                />
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ))}
                    </Col>
                  )}

                  {/* Payment type + the field it unlocks */}
                  <Col md={6} lg={4}>
                    <Form.Label>Payment Type</Form.Label>
                    <Controller
                      name="payment_type_id"
                      control={control}
                      rules={{ required: "Select Payment Type" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={PAYMENT_TYPES}
                          styles={selectStyles(!!errors.payment_type_id)}
                        />
                      )}
                    />
                    <FieldError error={errors.payment_type_id} />
                  </Col>

                  {paymentType === "UPI" && (
                    <Col md={6} lg={4}>
                      <Form.Label>UPI Id</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="UPI ID"
                        isInvalid={!!errors.upi_id}
                        {...register("upi_id", {
                          required: "UPI ID is required",
                        })}
                      />
                      <FieldError error={errors.upi_id} />
                    </Col>
                  )}

                  {paymentType === "Crypto" && (
                    <Col md={6} lg={4}>
                      <Form.Label>Network</Form.Label>
                      <Controller
                        name="network_id"
                        control={control}
                        rules={{ required: "Network is required" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={networks}
                            styles={selectStyles(!!errors.network_id)}
                          />
                        )}
                      />
                      <FieldError error={errors.network_id} />
                    </Col>
                  )}
                </Row>

                {paymentType === "Bank" && (
                  <>
                    <h5 className="mb-3">
                      <b>Bank Details</b>
                    </h5>
                    <Row className="g-3">
                      {textField("holder_name", "Account Name", "Account Name")}
                      {textField("bank_name", "Bank Name", "Bank Name")}
                      {textField("firm_name", "Branch", "Branch")}
                      {textField("ac_no", "A/c No", "A/c No", "number")}
                      {textField("ifsc_code", "IFSC Code", "IFSC Code")}

                      <Col md={6} lg={3}>
                        <Form.Label>Text Color</Form.Label>
                        <Form.Control
                          type="color"
                          className="form-control-color"
                          {...register("text_color")}
                        />
                      </Col>

                      <Col md={6} lg={3}>
                        <Form.Label>Background Color</Form.Label>
                        <Form.Control
                          type="color"
                          className="form-control-color"
                          {...register("background_color")}
                        />
                      </Col>

                      <Col md={6} lg={3}>
                        <Form.Label>Icon</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        {iconPreview && (
                          <div className="mt-2 text-center">
                            <img
                              src={iconPreview}
                              alt="Icon preview"
                              width={60}
                              height={60}
                              className="rounded-circle"
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </>
                )}

                <hr />
                <div className="d-flex justify-content-center pb-3">
                  <CancelButton name="cancel" handleClose={props.handleClose} />
                  <SaveButton name="save" />
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Offcanvas.Body>
      </Offcanvas>

      <ModalSave
        message={showModal.message}
        showErrorModal={showModal.code ? true : false}
      />
    </>
  );
};

export default AddOffCanvance;
